<!-- eslint-disable no-underscore-dangle -->
<script setup>
import { ref, computed, onMounted } from 'vue';
import { useI18n } from 'vue-i18n';
import { useStore, useMapGetter } from 'dashboard/composables/store';
import Button from 'dashboard/components-next/button/Button.vue';

const props = defineProps({
  funnel: { type: Object, default: null },
});

const emit = defineEmits(['close', 'save']);
const { t } = useI18n();
const store = useStore();

const name = ref(props.funnel?.name || '');
const description = ref(props.funnel?.description || '');
const stages = ref(
  props.funnel?.funnel_stages?.map(s => ({ ...s })) || [
    { name: 'Lead', color: '#3B82F6', position: 0 },
    { name: 'Contato', color: '#F59E0B', position: 1 },
    { name: 'Proposta', color: '#8B5CF6', position: 2 },
    { name: 'Negociação', color: '#EC4899', position: 3 },
    { name: 'Fechado', color: '#10B981', position: 4 },
  ]
);

// Sharing state
const selectedShares = ref([]);
const showShareDropdown = ref(false);
const shareSearch = ref('');

const agents = useMapGetter('agents/getAgents');
const teams = useMapGetter('teams/getMyTeams');
const currentUser = useMapGetter('getCurrentUser');

onMounted(() => {
  store.dispatch('agents/get');
  store.dispatch('teams/get');
  // Pre-populate shared_with from existing funnel
  if (props.funnel?.shared_with?.length) {
    selectedShares.value = props.funnel.shared_with.map(s => ({
      type: s.type,
      id: s.id,
      name: s.name,
    }));
  }
});

const shareableItems = computed(() => {
  const items = [];
  const query = shareSearch.value.toLowerCase();

  // Add agents (exclude current user / funnel creator)
  (agents.value || []).forEach(agent => {
    if (agent.id === currentUser.value?.id) return;
    if (query && !agent.name.toLowerCase().includes(query)) return;
    items.push({
      type: 'User',
      id: agent.id,
      name: agent.name,
      group: 'agents',
    });
  });

  // Add teams
  (teams.value || []).forEach(team => {
    if (query && !team.name.toLowerCase().includes(query)) return;
    items.push({ type: 'Team', id: team.id, name: team.name, group: 'teams' });
  });

  return items;
});

const isSelected = (type, id) => {
  return selectedShares.value.some(s => s.type === type && s.id === id);
};

const toggleShare = item => {
  const idx = selectedShares.value.findIndex(
    s => s.type === item.type && s.id === item.id
  );
  if (idx !== -1) {
    selectedShares.value.splice(idx, 1);
  } else {
    selectedShares.value.push({
      type: item.type,
      id: item.id,
      name: item.name,
    });
  }
};

const removeShare = (type, id) => {
  selectedShares.value = selectedShares.value.filter(
    s => !(s.type === type && s.id === id)
  );
};

const colorPresets = [
  '#3B82F6',
  '#06B6D4',
  '#10B981',
  '#F59E0B',
  '#EF4444',
  '#8B5CF6',
  '#EC4899',
  '#6B7280',
  '#F97316',
  '#14B8A6',
];

const isEditing = computed(() => !!props.funnel?.id);

const dragIndex = ref(null);
const dropIndex = ref(null);

const visibleStages = computed(() =>
  stages.value
    .map((s, i) => ({ stage: s, _originalIndex: i }))
    .filter(({ stage }) => !stage._destroy)
);

const onDragStart = (index, event) => {
  dragIndex.value = index;
  event.dataTransfer.effectAllowed = 'move';
};

const onDragOver = (index, event) => {
  event.preventDefault();
  event.dataTransfer.dropEffect = 'move';
  dropIndex.value = index;
};

const onDragEnd = () => {
  dragIndex.value = null;
  dropIndex.value = null;
};

const onDrop = targetIndex => {
  if (dragIndex.value === null || dragIndex.value === targetIndex) {
    onDragEnd();
    return;
  }
  const visible = visibleStages.value.map(item => item.stage);
  const [moved] = visible.splice(dragIndex.value, 1);
  visible.splice(targetIndex, 0, moved);
  const destroyed = stages.value.filter(s => s._destroy);
  visible.forEach((s, i) => {
    s.position = i;
  });
  stages.value = [...visible, ...destroyed];
  onDragEnd();
};

const addStage = () => {
  stages.value.push({
    name: '',
    color: colorPresets[stages.value.length % colorPresets.length],
    position: stages.value.length,
  });
};

const removeStage = index => {
  const stage = stages.value[index];
  if (stage.id) {
    stage._destroy = true;
  } else {
    stages.value.splice(index, 1);
  }
  stages.value
    .filter(s => !s._destroy)
    .forEach((s, i) => {
      s.position = i;
    });
};

const onSubmit = () => {
  if (!name.value.trim()) return;
  emit('save', {
    id: props.funnel?.id,
    funnel: {
      name: name.value,
      description: description.value,
      funnel_stages_attributes: stages.value.map(s => ({
        id: s.id,
        name: s.name,
        color: s.color,
        position: s.position,
        _destroy: s._destroy || false,
      })),
    },
    shared_with: selectedShares.value.map(s => ({
      type: s.type,
      id: s.id,
    })),
  });
};
</script>

<template>
  <div class="flex flex-col h-auto overflow-auto">
    <woot-modal-header
      :header-title="
        isEditing ? t('CRM.SETTINGS.EDIT_FUNNEL') : t('CRM.SETTINGS.ADD_FUNNEL')
      "
    />
    <form class="w-full" @submit.prevent="onSubmit">
      <!-- Name -->
      <div class="w-full">
        <label
          class="flex items-center gap-1.5 text-sm font-medium text-n-slate-12 mb-1"
        >
          <span class="i-lucide-type size-4 text-n-slate-10" />
          {{ t('CRM.SETTINGS.FUNNEL_NAME') }}
        </label>
        <input
          v-model="name"
          type="text"
          :placeholder="t('CRM.SETTINGS.FUNNEL_NAME_PLACEHOLDER')"
          class="w-full rounded-lg border border-n-weak bg-n-background px-3 py-2 text-sm text-n-slate-12 placeholder:text-n-slate-9 focus:ring-2 focus:ring-n-blue-7 focus:border-n-blue-7 transition-colors"
        />
      </div>

      <!-- Description -->
      <div class="w-full mt-3">
        <label
          class="flex items-center gap-1.5 text-sm font-medium text-n-slate-12 mb-1"
        >
          <span class="i-lucide-file-text size-4 text-n-slate-10" />
          {{ t('CRM.SETTINGS.FUNNEL_DESCRIPTION') }}
        </label>
        <textarea
          v-model="description"
          rows="2"
          :placeholder="t('CRM.SETTINGS.FUNNEL_DESCRIPTION_PLACEHOLDER')"
          class="w-full rounded-lg border border-n-weak bg-n-background px-3 py-2 text-sm text-n-slate-12 placeholder:text-n-slate-9 resize-none focus:ring-2 focus:ring-n-blue-7 focus:border-n-blue-7 transition-colors"
        />
      </div>

      <!-- Share with agents/teams -->
      <div class="w-full mt-4">
        <label
          class="flex items-center gap-1.5 text-sm font-medium text-n-slate-12 mb-1"
        >
          <span class="i-lucide-users size-4 text-n-slate-10" />
          {{ t('CRM.SETTINGS.SHARE_WITH') }}
        </label>

        <!-- Selected chips -->
        <div v-if="selectedShares.length" class="flex flex-wrap gap-1.5 mb-2">
          <span
            v-for="share in selectedShares"
            :key="`${share.type}-${share.id}`"
            class="inline-flex items-center gap-1 px-2 py-1 rounded-full text-xs font-medium"
            :class="
              share.type === 'Team'
                ? 'bg-n-violet-2 text-n-violet-11'
                : 'bg-n-blue-2 text-n-blue-11'
            "
          >
            <span
              class="size-3.5"
              :class="
                share.type === 'Team' ? 'i-lucide-users-round' : 'i-lucide-user'
              "
            />
            {{ share.name }}
            <button
              type="button"
              class="ml-0.5 hover:opacity-70"
              @click="removeShare(share.type, share.id)"
            >
              <span class="i-lucide-x size-3" />
            </button>
          </span>
        </div>

        <!-- Dropdown trigger -->
        <div class="relative">
          <input
            v-model="shareSearch"
            type="text"
            :placeholder="t('CRM.SETTINGS.SHARE_SEARCH_PLACEHOLDER')"
            class="w-full rounded-lg border border-n-weak bg-n-background px-3 py-2 text-sm text-n-slate-12 placeholder:text-n-slate-9 focus:ring-2 focus:ring-n-blue-7 focus:border-n-blue-7 transition-colors"
            @focus="showShareDropdown = true"
          />

          <!-- Dropdown -->
          <div
            v-if="showShareDropdown"
            class="absolute z-50 mt-1 w-full max-h-48 overflow-y-auto rounded-lg border border-n-weak bg-n-background shadow-lg"
          >
            <!-- Teams group -->
            <div v-if="shareableItems.some(i => i.group === 'teams')">
              <div
                class="px-3 py-1.5 text-[11px] font-semibold text-n-slate-9 uppercase tracking-wider bg-n-alpha-1"
              >
                {{ t('CRM.PERMISSIONS.TEAM') }}
              </div>
              <button
                v-for="item in shareableItems.filter(i => i.group === 'teams')"
                :key="`team-${item.id}`"
                type="button"
                class="flex items-center gap-2 w-full px-3 py-2 text-sm text-n-slate-12 hover:bg-n-alpha-2 transition-colors"
                @click="toggleShare(item)"
              >
                <span
                  class="flex items-center justify-center size-4 rounded border transition-colors"
                  :class="
                    isSelected(item.type, item.id)
                      ? 'bg-n-blue-9 border-n-blue-9'
                      : 'border-n-slate-7'
                  "
                >
                  <span
                    v-if="isSelected(item.type, item.id)"
                    class="i-lucide-check size-3 text-white"
                  />
                </span>
                <span class="i-lucide-users-round size-3.5 text-n-violet-10" />
                {{ item.name }}
              </button>
            </div>

            <!-- Agents group -->
            <div v-if="shareableItems.some(i => i.group === 'agents')">
              <div
                class="px-3 py-1.5 text-[11px] font-semibold text-n-slate-9 uppercase tracking-wider bg-n-alpha-1"
              >
                {{ t('CRM.PERMISSIONS.AGENT') }}
              </div>
              <button
                v-for="item in shareableItems.filter(i => i.group === 'agents')"
                :key="`agent-${item.id}`"
                type="button"
                class="flex items-center gap-2 w-full px-3 py-2 text-sm text-n-slate-12 hover:bg-n-alpha-2 transition-colors"
                @click="toggleShare(item)"
              >
                <span
                  class="flex items-center justify-center size-4 rounded border transition-colors"
                  :class="
                    isSelected(item.type, item.id)
                      ? 'bg-n-blue-9 border-n-blue-9'
                      : 'border-n-slate-7'
                  "
                >
                  <span
                    v-if="isSelected(item.type, item.id)"
                    class="i-lucide-check size-3 text-white"
                  />
                </span>
                <span class="i-lucide-user size-3.5 text-n-blue-10" />
                {{ item.name }}
              </button>
            </div>

            <!-- Empty -->
            <div
              v-if="!shareableItems.length"
              class="px-3 py-4 text-sm text-n-slate-9 text-center"
            >
              {{ t('CRM.SETTINGS.SHARE_NO_RESULTS') }}
            </div>
          </div>
        </div>

        <!-- Click outside to close -->
        <div
          v-if="showShareDropdown"
          class="fixed inset-0 z-40"
          @click="showShareDropdown = false"
        />

        <p class="text-xs text-n-slate-9 mt-1">
          {{ t('CRM.SETTINGS.SHARE_HINT') }}
        </p>
      </div>

      <!-- Stages -->
      <div class="w-full mt-4">
        <div class="flex items-center justify-between mb-3">
          <span class="text-sm font-medium text-n-slate-12">
            {{ t('CRM.SETTINGS.STAGES') }}
          </span>
          <Button
            type="button"
            size="sm"
            faded
            icon="i-lucide-plus"
            :label="t('CRM.SETTINGS.ADD_STAGE')"
            @click="addStage"
          />
        </div>
        <div class="flex flex-col gap-1">
          <div
            v-for="(item, index) in visibleStages"
            :key="item._originalIndex"
            draggable="true"
            class="flex items-center gap-2 p-2 rounded-lg hover:bg-n-alpha-1 transition-all border-2 border-transparent"
            :class="{
              'opacity-50': dragIndex === index,
              'border-t-n-blue-7':
                dropIndex === index && dragIndex !== null && dragIndex > index,
              'border-b-n-blue-7':
                dropIndex === index && dragIndex !== null && dragIndex < index,
            }"
            @dragstart="onDragStart(index, $event)"
            @dragover="onDragOver(index, $event)"
            @drop="onDrop(index)"
            @dragend="onDragEnd"
          >
            <!-- Drag handle -->
            <span
              class="i-lucide-grip-vertical size-4 text-n-slate-8 cursor-grab active:cursor-grabbing flex-shrink-0"
            />

            <!-- Position number -->
            <span
              class="text-[11px] font-medium text-n-slate-9 w-4 text-center flex-shrink-0"
            >
              {{ index + 1 }}
            </span>

            <!-- Color swatch + picker -->
            <div class="relative flex-shrink-0">
              <span
                class="block size-8 rounded-lg border border-n-weak cursor-pointer"
                :style="{ backgroundColor: item.stage.color }"
              />
              <input
                v-model="item.stage.color"
                type="color"
                class="absolute inset-0 opacity-0 cursor-pointer size-8"
              />
            </div>

            <!-- Color presets -->
            <div class="flex gap-1 flex-shrink-0">
              <button
                v-for="preset in colorPresets.slice(0, 5)"
                :key="preset"
                type="button"
                class="size-4 rounded-full transition-transform hover:scale-125"
                :class="
                  item.stage.color === preset
                    ? 'ring-2 ring-n-brand ring-offset-1'
                    : ''
                "
                :style="{ backgroundColor: preset }"
                @click="item.stage.color = preset"
              />
            </div>

            <!-- Name input -->
            <input
              v-model="item.stage.name"
              type="text"
              class="flex-1 rounded-lg border border-n-weak bg-n-background px-3 py-1.5 text-sm text-n-slate-12 placeholder:text-n-slate-9 focus:ring-2 focus:ring-n-blue-7 focus:border-n-blue-7 transition-colors"
              :placeholder="t('CRM.SETTINGS.STAGE_NAME_PLACEHOLDER')"
            />

            <!-- Delete -->
            <button
              type="button"
              class="size-7 grid place-content-center rounded-md hover:bg-n-ruby-2 flex-shrink-0 transition-colors"
              @click="removeStage(item._originalIndex)"
            >
              <span
                class="i-lucide-trash-2 size-3.5 text-n-slate-10 hover:text-n-ruby-11"
              />
            </button>
          </div>
        </div>

        <!-- Stage preview -->
        <div
          v-if="visibleStages.length > 0"
          class="flex gap-1 mt-3 p-2 bg-n-alpha-1 rounded-lg overflow-x-auto"
        >
          <div
            v-for="item in visibleStages"
            :key="item.stage.position"
            class="flex items-center gap-1.5 px-2 py-1 rounded-md text-[11px] font-medium text-n-slate-11 bg-white dark:bg-n-slate-3 shadow-sm flex-shrink-0"
          >
            <span
              class="size-2 rounded-full"
              :style="{ backgroundColor: item.stage.color }"
            />
            {{ item.stage.name || '...' }}
          </div>
        </div>
      </div>

      <div class="flex justify-end gap-2 py-3 mt-4">
        <Button
          faded
          slate
          type="button"
          :label="t('CRM.SETTINGS.CANCEL')"
          @click="emit('close')"
        />
        <Button type="submit" :label="t('CRM.SETTINGS.SAVE')" />
      </div>
    </form>
  </div>
</template>
